import { Helmet } from "react-helmet-async";
import { PageHero } from "@/components/shared/PageHero";
import { ServiceSidebar } from "@/components/services/ServiceSidebar";
import { CTASection } from "@/components/shared/CTASection";
import { ServiceFAQ } from "@/components/services/ServiceFAQ";
import { mulchingFaqs } from "@/data/serviceFaqs";
import { Layers, Flower2, Droplets, Shield, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import mulchImage from "@/assets/services/mulching-boulder.jpg";

const features = [
  {
    icon: Layers,
    title: "Premium Mulch",
    description: "Quality hardwood, cedar, and colored mulches installed at the proper depth for your beds.",
  },
  {
    icon: Flower2,
    title: "Bed Design",
    description: "Create beautiful new flower beds or refresh existing ones with proper edging and preparation.",
  },
  {
    icon: Shield,
    title: "Weed Suppression",
    description: "Proper mulching reduces weed growth and keeps your beds looking clean all season.",
  },
  {
    icon: Droplets,
    title: "Moisture Retention",
    description: "Mulch helps retain soil moisture, reducing watering needs and protecting plant roots.",
  },
];

const Mulching = () => {
  return (
    <>
      <Helmet>
        <title>Mulching & Bed Services | Riekels Lawn Care & Landscaping</title>
        <meta
          name="description"
          content="Professional mulching and flower bed services in the West Twin Cities Metro, MN. Premium mulch installation and bed maintenance. Call (763) 245-7813."
        />
      </Helmet>
      <main>
        <PageHero
          badge="Our Services"
          title="Mulching & Beds"
          subtitle="Transform your landscape beds with fresh mulch and professional maintenance."
        />

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
              <div className="lg:col-span-1">
                <ServiceSidebar />
              </div>
              <div className="lg:col-span-3">
                <div className="mb-8 rounded-xl overflow-hidden shadow-lg">
                  <img 
                    src={mulchImage} 
                    alt="Beautiful mulched landscape bed with boulders" 
                    className="w-full h-64 md:h-80 object-cover object-center"
                  />
                </div>

                <div className="prose prose-lg max-w-none mb-12">
                  <h2 className="text-3xl font-heading font-bold text-foreground mb-6">
                    Beautiful Beds Start With Quality Mulch
                  </h2>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Fresh mulch is one of the quickest ways to dramatically improve your landscape's appearance. 
                    Beyond looks, mulch provides essential benefits for plant health and reduces maintenance.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    We offer a variety of mulch options and can help you choose the right color and material 
                    for your property. Our team installs mulch at the proper depth and ensures it's kept away 
                    from plant stems to prevent rot.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {features.map((feature, index) => (
                    <article key={index} className="feature-card">
                      <div className="icon-circle mb-4">
                        <feature.icon className="w-6 h-6" />
                      </div>
                      <h3 className="text-lg font-heading font-bold text-foreground mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-muted-foreground">
                        {feature.description}
                      </p>
                    </article>
                  ))}
                </div>

                <div className="mt-12 bg-secondary rounded-2xl p-8 text-center">
                  <Layers className="w-12 h-12 mx-auto mb-4 text-secondary-foreground" />
                  <h3 className="text-2xl font-heading font-bold text-secondary-foreground mb-3">
                    Refresh Your Landscape Beds
                  </h3>
                  <p className="text-secondary-foreground/80 mb-6 max-w-lg mx-auto">
                    Get a quote for mulch installation or complete bed renovation.
                  </p>
                  <Link to="/contact">
                    <Button size="lg" className="bg-white hover:bg-white/90 text-secondary font-semibold">
                      Request a Quote <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <ServiceFAQ faqs={mulchingFaqs} />

        <CTASection />
      </main>
    </>
  );
};

export default Mulching;
